import { Chip, MenuItem, Stack, TextField, Typography } from "@mui/material";

import { FEED_SORTS, FeedSortKey, KindFilterOption } from "./complianceFeed";

/**
 * The feed's controls: a horizontal row of clickable type filters with counts,
 * then the sort selector.
 *
 * Filters are single-select against "All" rather than checkboxes — the counts
 * make each option a direct answer to "how many of these do I have", and one
 * click both narrows the list and reads back the total.
 */
export function ComplianceFeedControls({
  options,
  totalCount,
  selectedKind,
  onSelectKind,
  sortKey,
  onSortChange,
}: {
  options: readonly KindFilterOption[];
  totalCount: number;
  /** null = All. */
  selectedKind: string | null;
  onSelectKind: (kind: string | null) => void;
  sortKey: FeedSortKey;
  onSortChange: (key: FeedSortKey) => void;
}) {
  return (
    <Stack
      direction="row"
      spacing={2}
      alignItems="center"
      justifyContent="space-between"
      flexWrap="wrap"
      useFlexGap
    >
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="body2" color="text.secondary" sx={{ mr: 0.5 }}>
          Type
        </Typography>
        <FilterChip
          label={`All (${totalCount})`}
          selected={selectedKind === null}
          onClick={() => onSelectKind(null)}
        />
        {options.map((option) => (
          <FilterChip
            key={option.label}
            label={`${option.label} (${option.count})`}
            selected={selectedKind === option.label}
            onClick={() => onSelectKind(option.label)}
          />
        ))}
      </Stack>

      <TextField
        select
        size="small"
        label="Sort by"
        value={sortKey}
        onChange={(event) => {
          const value = String(event.target.value);
          const sort = FEED_SORTS.find((s) => s.key === value);
          if (sort) onSortChange(sort.key);
        }}
        sx={{ minWidth: 200 }}
      >
        {FEED_SORTS.map((sort) => (
          <MenuItem key={sort.key} value={sort.key}>
            {sort.label}
          </MenuItem>
        ))}
      </TextField>
    </Stack>
  );
}

/**
 * Reads as a clickable link-chip: filled while active, outlined otherwise.
 * `aria-pressed` rather than a plain button role so the toggle state is exposed
 * to assistive tech, matching DiscrepancySummaryBar's tiles.
 */
function FilterChip({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <Chip
      label={label}
      size="small"
      onClick={onClick}
      aria-pressed={selected}
      color={selected ? "primary" : "default"}
      variant={selected ? "filled" : "outlined"}
      sx={{ fontWeight: selected ? 600 : 400 }}
    />
  );
}
